import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/lib/auth';
import { useSyncGuruTimezone } from '@/lib/hooks/useSyncGuruTimezone';

/**
 * Verifica que el usuario tenga rol 'guru' o 'admin' antes de mostrar
 * /guru/*. Admin puede entrar para QA y para ver las colas que reusan
 * páginas del guru (ej. /guru/grading para calificar las tareas de los
 * 275 cursos importados cuyo dueño es el usuario sistema "Gurumba").
 */
export function GuruGuard({ children }: { children: React.ReactNode }) {
  const user = useUser();
  // La zona horaria del guru sigue a su dispositivo (sus horas no cambian).
  useSyncGuruTimezone();
  const { data, isLoading } = useQuery({
    queryKey: ['has-role-guru-or-admin', user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['guru', 'admin']);
      if (error) throw error;
      return !!data && data.length > 0;
    },
    enabled: !!user,
  });

  if (!user) return <Navigate to="/login" replace />;
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold">Acceso restringido</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Esta sección es solo para Gurus aprobados y administradores.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
